import { useState, useEffect } from 'react';

/**
 * Hook to manage Clerk authentication state
 * Returns user ID and authentication status
 */
export function useClerkAuth() {
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [clerkUserId, setClerkUserId] = useState(null); // Clerk user ID (for access checks)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingSession, setIsProcessingSession] = useState(false); // Track if we're processing session_id

  // Helper function to extract username from email (part before @)
  const extractEmailUsername = (email) => {
    if (!email || typeof email !== 'string') return null;
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return email; // If no @, return whole string
    return email.substring(0, atIndex);
  };

  // Helper function to verify auth via backend
  const verifyWithBackend = async (sessionId) => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.userId) {
          return {
            userId: data.userId, // This is the Clerk user ID from backend
            email: data.email || null,
            clerkUserId: data.userId // Store Clerk user ID separately
          };
        } else {
          return null;
        }
      } else if (response.status === 401) {
        return null;
      } else {
        console.error('useClerkAuth: Backend verification error:', response.status);
        return null;
      }
    } catch (error) {
      console.error('useClerkAuth: Backend verification exception:', error);
      return null;
    }
  };

  // Helper function to extract session ID from JWT token (basic decoding)
  const extractSessionIdFromJWT = (token) => {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Decode payload (base64url)
      const payload = parts[1];
      // Add padding if needed
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
      const payloadData = JSON.parse(decoded);
      
      // Look for session ID in common JWT claims
      // Clerk JWTs might have 'sid' (session ID) or 'sub' (user ID) or other fields
      return payloadData.sid || payloadData.session_id || null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to get session ID from Clerk SDK or cookies/localStorage
  const getSessionId = async () => {
    try {
      // Method 1: Try to get from Clerk SDK first
      if (window.Clerk) {
        const clerk = window.Clerk;
        
        // If Clerk is loaded, try to get session
        if (clerk.loaded) {
          try {
            const session = await clerk.session;
            if (session && session.id) {
              return session.id;
            }
          } catch (e) {
            // Continue to other methods
          }
        }
        
        // Try to load Clerk first if not loaded (might fail due to domain)
        if (!clerk.loaded) {
          try {
            await clerk.load();
            const session = await clerk.session;
            if (session && session.id) {
              return session.id;
            }
          } catch (e) {
            // Continue to other methods
          }
        }
      }
      
      // Method 2: Try to get from localStorage (Clerk might store session info)
      try {
        const clerkStorage = localStorage.getItem('__clerk_db_jwt');
        if (clerkStorage) {
          // Try to parse and extract session ID
          try {
            const parsed = JSON.parse(clerkStorage);
            if (parsed && parsed.session) {
              const sessionId = parsed.session.id || parsed.sessionId;
              if (sessionId) {
                return sessionId;
              }
            }
          } catch (e) {
            // If not JSON, might be JWT - try to decode
            const sessionId = extractSessionIdFromJWT(clerkStorage);
            if (sessionId) {
              return sessionId;
            }
          }
        }
        
        // Check for other Clerk localStorage keys
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('__clerk')) {
            try {
              const value = localStorage.getItem(key);
              if (value) {
                const sessionId = extractSessionIdFromJWT(value);
                if (sessionId) {
                  return sessionId;
                }
                // Try parsing as JSON
                try {
                  const parsed = JSON.parse(value);
                  if (parsed?.session?.id || parsed?.sessionId) {
                    const sessionId = parsed.session?.id || parsed.sessionId;
                    return sessionId;
                  }
                } catch (e) {
                  // Not JSON, continue
                }
              }
            } catch (e) {
              // Continue checking other keys
            }
          }
        }
      } catch (error) {
        // Continue to other methods
      }
      
      // Method 3: Try to get from cookies (might work if cookies are accessible)
      try {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name && value && (name.includes('clerk') || name.includes('session'))) {
            // Try to extract session ID from JWT if it's a JWT token
            const sessionId = extractSessionIdFromJWT(value);
            if (sessionId) {
              return sessionId;
            }
          }
        }
      } catch (error) {
        // Continue to other methods
      }
      
      // Method 4: Check URL parameters (in case user was redirected with session ID)
      // This is the PRIMARY method - user clicks "Go to Trading" on amberhorizon.io
      // which redirects to amberhorizon.eu?session_id=xxx
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id') || urlParams.get('sid');
        if (sessionId) {
          // Clean up URL by removing the session_id parameter for security
          if (window.history.replaceState) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('session_id');
            newUrl.searchParams.delete('sid');
            window.history.replaceState({}, '', newUrl.toString());
          }
          return sessionId;
        }
      } catch (error) {
        // Continue to other methods
      }
      
      
      console.warn('useClerkAuth: Could not extract session ID from any source');
      return null;
    } catch (error) {
      console.error('useClerkAuth: Error getting session ID:', error);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    let checkInterval;
    let domainErrorDetected = false;
    let backendFallbackAttempted = false;

    // IMMEDIATE BYPASS CHECK: Check for bypass parameter BEFORE any async operations
    // This ensures it happens synchronously and prevents redirects
    const urlParams = new URLSearchParams(window.location.search);
    const bypassValue = urlParams.get('bypass');
    const hasBypassParam = bypassValue === 'louisjanb@gmail.com';
    
    if (hasBypassParam) {
      // Clean up URL immediately
      if (window.history.replaceState) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('bypass');
        window.history.replaceState({}, '', newUrl.toString());
      }
      
      // Set temporary auth IMMEDIATELY (synchronously)
      const tempEmail = 'louisjanb@gmail.com';
      const tempUserId = extractEmailUsername(tempEmail); // Use email username instead of Clerk ID
      
      setUserId(tempUserId);
      setUserEmail(tempEmail);
      setIsAuthenticated(true);
      setIsLoading(false);
      
      // Don't run any other auth checks
      return;
    }

    const checkClerkAuth = async () => {
      try {
        const currentDomain = window.location.hostname;
        
        // FIRST: Check if session_id is in URL (user came from dashboard)
        // This is the primary authentication method for amberhorizon.eu
        const currentUrlParams = new URLSearchParams(window.location.search);
        const sessionIdFromUrl = currentUrlParams.get('session_id') || currentUrlParams.get('sid');
        
        if (sessionIdFromUrl) {
          // Set flag that we're processing session_id
          setIsProcessingSession(true);
          
          // Clean up URL immediately (for security)
          if (window.history.replaceState) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('session_id');
            newUrl.searchParams.delete('sid');
            window.history.replaceState({}, '', newUrl.toString());
          }
          
          // Verify with backend
          const authResult = await verifyWithBackend(sessionIdFromUrl);
          if (authResult && mounted) {
            // Extract username from email for userId (for trading)
            const emailUsername = extractEmailUsername(authResult.email) || authResult.userId;
            setUserId(emailUsername);
            setUserEmail(authResult.email);
            // Store Clerk user ID separately (for access checks)
            setClerkUserId(authResult.clerkUserId || authResult.userId);
            setIsAuthenticated(true);
            setIsLoading(false);
            setIsProcessingSession(false);
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
            return;
          } else {
            // Backend verification failed
            if (mounted) {
              setIsLoading(false);
              setIsAuthenticated(false);
              setIsProcessingSession(false);
            }
            return;
          }
        }
        
        // If we're on amberhorizon.eu domain (root or subdomain) and no session_id in URL
        // Clerk SDK won't work on these domains, so redirect to marketing site
        // BUT: Wait a bit to ensure session_id from URL is processed first
        if (currentDomain === 'amberhorizon.eu' || currentDomain.endsWith('.amberhorizon.eu')) {
          // Double-check URL params one more time (in case they weren't read correctly)
          const doubleCheckParams = new URLSearchParams(window.location.search);
          const doubleCheckSessionId = doubleCheckParams.get('session_id') || doubleCheckParams.get('sid');
          
          if (!doubleCheckSessionId) {
            if (mounted) {
              // Guest access: allow anyone to view the trading platform without an account.
              // clerkUserId=null causes App.js to skip the instrument access check (fail-open).
              setUserId('guest');
              setUserEmail(null);
              setClerkUserId(null);
              setIsAuthenticated(true);
              setIsLoading(false);
            }
          }
          return;
        }
        
        // For other domains (e.g., localhost, amberhorizon.io), try Clerk SDK
        // Wait for Clerk to load
        if (!window.Clerk) {
          return; // Still loading, wait for next check
        }

        const clerk = window.Clerk;

        // First, check if user is already available without calling load()
        // This works if Clerk was initialized on a different tab/page
        if (clerk.user && clerk.user.id) {
          if (mounted) {
            const email = clerk.user.emailAddresses?.[0]?.emailAddress || null;
            // Extract username from email for userId (for trading)
            const emailUsername = extractEmailUsername(email) || clerk.user.id;
            setUserId(emailUsername);
            setUserEmail(email);
            // Store Clerk user ID separately (for access checks)
            setClerkUserId(clerk.user.id);
            setIsAuthenticated(true);
            setIsLoading(false);
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
          }
          return;
        }

        // If not already available, try to load Clerk
        // This might fail on amberhorizon.eu if Clerk is only configured for amberhorizon.io
        if (!clerk.loaded) {
          try {
            await clerk.load();
          } catch (loadError) {
            console.error('useClerkAuth: Error loading Clerk:', loadError);
            
            // Check for domain mismatch error
            const errorMessage = loadError.message || loadError.toString() || '';
            if (errorMessage.includes('domain') || errorMessage.includes('Domain') || errorMessage.includes('amberhorizon.io')) {
              domainErrorDetected = true;
              
              // Try backend fallback (only once)
              if (!backendFallbackAttempted) {
                backendFallbackAttempted = true;
                
                // First, check if session_id is in URL (user came from dashboard redirect)
                const urlParams = new URLSearchParams(window.location.search);
                const sessionIdFromUrl = urlParams.get('session_id') || urlParams.get('sid');
                
                let sessionId = null;
                
                if (sessionIdFromUrl) {
                  sessionId = sessionIdFromUrl;
                } else {
                  // Try to get from other sources
                  sessionId = await getSessionId();
                }
                
                if (sessionId) {
                  const authResult = await verifyWithBackend(sessionId);
                  if (authResult && mounted) {
                    // Extract username from email for userId (for trading)
                    const emailUsername = extractEmailUsername(authResult.email) || authResult.userId;
                    setUserId(emailUsername);
                    setUserEmail(authResult.email);
                    // Store Clerk user ID separately (for access checks)
                    setClerkUserId(authResult.clerkUserId || authResult.userId);
                    setIsAuthenticated(true);
                    setIsLoading(false);
                    if (checkInterval) {
                      clearInterval(checkInterval);
                      checkInterval = null;
                    }
                    return;
                  }
                } else {
                  // If no session ID found and user directly visited amberhorizon.eu,
                  // redirect them to marketing site to sign in
                  if (mounted) {
                    setIsLoading(false);
                    setIsAuthenticated(false);
                    // Redirect will happen in App.js after 5 seconds
                  }
                }
              }
              
              // Stop polling immediately
              if (checkInterval) {
                clearInterval(checkInterval);
                checkInterval = null;
              }
              
              if (mounted && !backendFallbackAttempted) {
                setIsLoading(false);
                setIsAuthenticated(false);
              }
              return;
            }
            
            if (mounted) {
              setIsLoading(false);
              setIsAuthenticated(false);
            }
            return;
          }
        }

        // Check if user is authenticated after loading
        const user = clerk.user;

        if (user && user.id && mounted) {
          // Extract username from email for userId (instead of Clerk user ID)
          const email = user.emailAddresses?.[0]?.emailAddress || null;
          const emailUsername = extractEmailUsername(email) || user.id;
          setUserId(emailUsername);
          setUserEmail(email);
          // Store Clerk user ID separately (for access checks)
          setClerkUserId(user.id);
          setIsAuthenticated(true);
          setIsLoading(false);
          
          // Clear polling interval once we have a user
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
          return;
        } else {
          // No user found - but only set as not authenticated if Clerk is fully loaded
          if (mounted && clerk.loaded) {
            setIsAuthenticated(false);
            setUserId(null);
            setUserEmail(null);
            setClerkUserId(null);
            setIsLoading(false);
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
          }
        }
      } catch (error) {
        console.error('useClerkAuth: Error checking authentication:', error);
        if (mounted) {
          setIsLoading(false);
          setIsAuthenticated(false);
        }
      }
    };

    // Initial check
    checkClerkAuth();

    // Poll every 500ms until Clerk loads or we get auth status
    checkInterval = setInterval(checkClerkAuth, 500);

    // Stop polling after 10 seconds to prevent infinite polling
    const timeout = setTimeout(() => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
      if (mounted && isLoading) {
        setIsLoading(false);
        // If Clerk loaded but no user, redirect will happen
        if (window.Clerk?.loaded && !window.Clerk?.user) {
          setIsAuthenticated(false);
        }
      }
    }, 10000);

    // Cleanup
    return () => {
      mounted = false;
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      clearTimeout(timeout);
    };
  }, []); // Only run on mount

  const signIn = () => {
    window.location.href = 'https://accounts.amberhorizon.io/sign-in?redirect_url=' + encodeURIComponent(window.location.href);
  };

  const signOut = async () => {
    try {
      if (window.Clerk && window.Clerk.user) {
        await window.Clerk.signOut();
      }
      window.location.href = 'https://amberhorizon.io/';
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = 'https://amberhorizon.io/';
    }
  };

  return {
    userId,
    userEmail,
    clerkUserId,
    isAuthenticated,
    isLoading,
    isProcessingSession,
    signIn,
    signOut
  };
}

